// Backend integration point: Replace localStorage with API calls to your backend

export interface Brand {
  id: string;
  name: string;
}

export interface Series {
  id: string;
  name: string;
  brandId: string;
}

export interface Model {
  id: string;
  name: string;
  seriesId: string;
}

export interface ErrorCategory {
  id: string;
  name: string;
  description?: string;
  modelId: string;
}

export interface PriceEntry {
  id: string;
  errorCategoryId: string;
  priceB2C: number;
  priceB2B?: number;
  durationDays: number;
  warrantyMonths: number;
}

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
  order: number;
}

export interface LegalPage {
  id: 'agb' | 'datenschutz' | 'widerruf';
  title: string;
  content: string;
  updatedAt: string;
}

const KEYS = {
  BRANDS: 'mr_brands',
  SERIES: 'mr_series',
  MODELS: 'mr_models',
  ERROR_CATEGORIES: 'mr_error_categories',
  PRICE_ENTRIES: 'mr_price_entries',
  FAQS: 'mr_faqs',
  LEGAL_PAGES: 'mr_legal_pages',
  INITIALIZED: 'mr_initialized',
  AUTH_TOKEN: 'mr_auth_token',
  ADMIN_PASSWORD: 'mr_admin_password',
};

const INITIAL_DATA = {
  brands: [
    { id: 'brand-001', name: 'Nintendo' },
    { id: 'brand-002', name: 'PlayStation' },
    { id: 'brand-003', name: 'Xbox' },
    { id: 'brand-004', name: 'Sega' },
    { id: 'brand-005', name: 'Atari' },
  ] as Brand[],

  series: [
    { id: 'series-001', name: 'Wii', brandId: 'brand-001' },
    { id: 'series-002', name: 'Wii U', brandId: 'brand-001' },
    { id: 'series-003', name: '3DS', brandId: 'brand-001' },
    { id: 'series-004', name: 'Switch', brandId: 'brand-001' },
    { id: 'series-005', name: 'GameCube', brandId: 'brand-001' },
    { id: 'series-006', name: 'Game Boy', brandId: 'brand-001' },
    { id: 'series-007', name: 'PlayStation 3', brandId: 'brand-002' },
    { id: 'series-008', name: 'PlayStation 4', brandId: 'brand-002' },
    { id: 'series-009', name: 'PlayStation 5', brandId: 'brand-002' },
    { id: 'series-010', name: 'PSP', brandId: 'brand-002' },
    { id: 'series-011', name: 'PS Vita', brandId: 'brand-002' },
    { id: 'series-012', name: 'Xbox 360', brandId: 'brand-003' },
    { id: 'series-013', name: 'Xbox One', brandId: 'brand-003' },
    { id: 'series-014', name: 'Xbox Series X/S', brandId: 'brand-003' },
    { id: 'series-015', name: 'Mega Drive', brandId: 'brand-004' },
    { id: 'series-016', name: 'Dreamcast', brandId: 'brand-004' },
    { id: 'series-017', name: 'Game Gear', brandId: 'brand-004' },
    { id: 'series-018', name: 'Atari 2600', brandId: 'brand-005' },
    { id: 'series-019', name: 'Atari 7800', brandId: 'brand-005' },
  ] as Series[],

  models: [
    { id: 'model-001', name: '3DS', seriesId: 'series-003' },
    { id: 'model-002', name: '3DS XL', seriesId: 'series-003' },
    { id: 'model-003', name: 'New 3DS', seriesId: 'series-003' },
    { id: 'model-004', name: 'New 3DS XL', seriesId: 'series-003' },
    { id: 'model-005', name: '2DS', seriesId: 'series-003' },
    { id: 'model-006', name: 'New 2DS XL', seriesId: 'series-003' },
    { id: 'model-007', name: 'Switch', seriesId: 'series-004' },
    { id: 'model-008', name: 'Switch Lite', seriesId: 'series-004' },
    { id: 'model-009', name: 'Switch OLED', seriesId: 'series-004' },
    { id: 'model-010', name: 'PlayStation 4', seriesId: 'series-008' },
    { id: 'model-011', name: 'PlayStation 4 Slim', seriesId: 'series-008' },
    { id: 'model-012', name: 'PlayStation 4 Pro', seriesId: 'series-008' },
  ] as Model[],

  errorCategories: [
    { id: 'err-001', name: 'Akku-Austausch', description: 'Akku defekt oder hält keine Ladung mehr', modelId: 'model-001' },
    { id: 'err-002', name: 'Display-Reparatur', description: 'Display gebrochen, Pixel defekt oder kein Bild', modelId: 'model-001' },
    { id: 'err-003', name: 'Display-Kratzer', description: 'Oberflächliche Kratzer auf dem Display', modelId: 'model-001' },
    { id: 'err-004', name: 'Scharnier-Reparatur', description: 'Scharnier gebrochen oder klemmt', modelId: 'model-001' },
    { id: 'err-005', name: 'Micro-SD-Slot-Reparatur', description: 'SD-Karte wird nicht erkannt oder Slot defekt', modelId: 'model-001' },
    { id: 'err-006', name: 'Kamera-Fehler', description: 'Kamera funktioniert nicht oder liefert kein Bild', modelId: 'model-001' },
    { id: 'err-007', name: 'Tasten-Reparatur', description: 'Tasten klemmen, reagieren nicht oder sind abgenutzt', modelId: 'model-001' },
    { id: 'err-008', name: 'Gehäuse-Reparatur', description: 'Gehäuse gebrochen oder stark zerkratzt', modelId: 'model-001' },
    { id: 'err-009', name: 'Joy-Con-Drift', description: 'Analogstick driftet ohne Eingabe', modelId: 'model-007' },
    { id: 'err-010', name: 'Akku-Austausch', description: 'Akku defekt oder hält keine Ladung mehr', modelId: 'model-007' },
    { id: 'err-011', name: 'Display-Reparatur', description: 'Display gebrochen oder kein Bild', modelId: 'model-007' },
    { id: 'err-012', name: 'Ladeport-Reparatur', description: 'USB-C Ladeport defekt oder lose', modelId: 'model-007' },
    { id: 'err-013', name: 'Gehäuse-Reparatur', description: 'Gehäuse beschädigt oder gebrochen', modelId: 'model-007' },
  ] as ErrorCategory[],

  priceEntries: [
    { id: 'price-001', errorCategoryId: 'err-001', priceB2C: 49.90, priceB2B: 35.00, durationDays: 7, warrantyMonths: 12 },
    { id: 'price-002', errorCategoryId: 'err-002', priceB2C: 79.90, priceB2B: 55.00, durationDays: 5, warrantyMonths: 6 },
    { id: 'price-003', errorCategoryId: 'err-003', priceB2C: 24.90, priceB2B: 17.00, durationDays: 2, warrantyMonths: 3 },
    { id: 'price-004', errorCategoryId: 'err-004', priceB2C: 39.90, priceB2B: 28.00, durationDays: 3, warrantyMonths: 6 },
    { id: 'price-005', errorCategoryId: 'err-005', priceB2C: 34.90, priceB2B: 24.00, durationDays: 2, warrantyMonths: 3 },
    { id: 'price-006', errorCategoryId: 'err-006', priceB2C: 44.90, priceB2B: 31.00, durationDays: 4, warrantyMonths: 6 },
    { id: 'price-007', errorCategoryId: 'err-007', priceB2C: 29.90, priceB2B: 20.00, durationDays: 2, warrantyMonths: 3 },
    { id: 'price-008', errorCategoryId: 'err-008', priceB2C: 54.90, priceB2B: 38.00, durationDays: 5, warrantyMonths: 6 },
    { id: 'price-009', errorCategoryId: 'err-009', priceB2C: 59.90, priceB2B: 42.00, durationDays: 5, warrantyMonths: 12 },
    { id: 'price-010', errorCategoryId: 'err-010', priceB2C: 69.90, priceB2B: 49.00, durationDays: 7, warrantyMonths: 12 },
    { id: 'price-011', errorCategoryId: 'err-011', priceB2C: 129.90, priceB2B: 89.00, durationDays: 7, warrantyMonths: 6 },
    { id: 'price-012', errorCategoryId: 'err-012', priceB2C: 49.90, priceB2B: 35.00, durationDays: 3, warrantyMonths: 6 },
    { id: 'price-013', errorCategoryId: 'err-013', priceB2C: 79.90, priceB2B: 55.00, durationDays: 5, warrantyMonths: 6 },
  ] as PriceEntry[],

  faqs: [
    {
      id: 'faq-001',
      question: 'Wie lange dauert eine Reparatur?',
      answer: 'Die meisten Reparaturen sind innerhalb von 2–7 Werktagen abgeschlossen. Die genaue Bearbeitungszeit hängt von der Reparaturart ab und wird Ihnen bei der Auftragsbestätigung mitgeteilt.',
      order: 1,
    },
    {
      id: 'faq-002',
      question: 'Welche Garantie erhalte ich auf die Reparatur?',
      answer: 'Wir gewähren je nach Reparaturart 3 bis 12 Monate Garantie auf alle durchgeführten Arbeiten und verwendeten Ersatzteile. Die genaue Garantiedauer ist auf der Preisliste angegeben.',
      order: 2,
    },
    {
      id: 'faq-003',
      question: 'Wie kann ich eine Reparatur anfragen?',
      answer: 'Wählen Sie auf unserer Startseite Ihre Konsole aus, klicken Sie auf „Reparatur anfragen" und füllen Sie das Formular aus. Wir melden uns dann schnellstmöglich mit einem Angebot und den nächsten Schritten bei Ihnen.',
      order: 3,
    },
    {
      id: 'faq-004',
      question: 'Gibt es spezielle Konditionen für Unternehmen (B2B)?',
      answer: 'Ja! Für Händler, Unternehmen und gewerbliche Kunden bieten wir individuelle B2B-Konditionen mit günstigeren Preisen und priorisierten Bearbeitungszeiten. Kontaktieren Sie uns einfach per E-Mail an info@mediarabbit.de für ein unverbindliches Gespräch.',
      order: 4,
    },
  ] as FaqItem[],
};

function safeLocalStorage() {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

export function initializeData(): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  if (ls.getItem(KEYS.INITIALIZED)) return;

  ls.setItem(KEYS.BRANDS, JSON.stringify(INITIAL_DATA.brands));
  ls.setItem(KEYS.SERIES, JSON.stringify(INITIAL_DATA.series));
  ls.setItem(KEYS.MODELS, JSON.stringify(INITIAL_DATA.models));
  ls.setItem(KEYS.ERROR_CATEGORIES, JSON.stringify(INITIAL_DATA.errorCategories));
  ls.setItem(KEYS.PRICE_ENTRIES, JSON.stringify(INITIAL_DATA.priceEntries));
  ls.setItem(KEYS.FAQS, JSON.stringify(INITIAL_DATA.faqs));
  ls.setItem(KEYS.INITIALIZED, 'true');
}

export function getBrands(): Brand[] {
  const ls = safeLocalStorage();
  if (!ls) return INITIAL_DATA.brands;
  const raw = ls.getItem(KEYS.BRANDS);
  return raw ? JSON.parse(raw) : [];
}

export function saveBrands(brands: Brand[]): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.setItem(KEYS.BRANDS, JSON.stringify(brands));
}

export function getSeries(): Series[] {
  const ls = safeLocalStorage();
  if (!ls) return INITIAL_DATA.series;
  const raw = ls.getItem(KEYS.SERIES);
  return raw ? JSON.parse(raw) : [];
}

export function saveSeries(series: Series[]): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.setItem(KEYS.SERIES, JSON.stringify(series));
}

export function getModels(): Model[] {
  const ls = safeLocalStorage();
  if (!ls) return INITIAL_DATA.models;
  const raw = ls.getItem(KEYS.MODELS);
  return raw ? JSON.parse(raw) : [];
}

export function saveModels(models: Model[]): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.setItem(KEYS.MODELS, JSON.stringify(models));
}

export function getErrorCategories(): ErrorCategory[] {
  const ls = safeLocalStorage();
  if (!ls) return INITIAL_DATA.errorCategories;
  const raw = ls.getItem(KEYS.ERROR_CATEGORIES);
  return raw ? JSON.parse(raw) : [];
}

export function saveErrorCategories(cats: ErrorCategory[]): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.setItem(KEYS.ERROR_CATEGORIES, JSON.stringify(cats));
}

export function getPriceEntries(): PriceEntry[] {
  const ls = safeLocalStorage();
  if (!ls) return INITIAL_DATA.priceEntries;
  const raw = ls.getItem(KEYS.PRICE_ENTRIES);
  return raw ? JSON.parse(raw) : [];
}

export function savePriceEntries(entries: PriceEntry[]): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.setItem(KEYS.PRICE_ENTRIES, JSON.stringify(entries));
}

export function formatEuro(value: number): string {
  return value.toFixed(2).replace('.', ',') + ' €';
}

// Auth helpers — Backend integration point: replace with JWT/session API
export function setAuthToken(token: string): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.setItem(KEYS.AUTH_TOKEN, token);
}

export function getAuthToken(): string | null {
  const ls = safeLocalStorage();
  if (!ls) return null;
  return ls.getItem(KEYS.AUTH_TOKEN);
}

export function clearAuthToken(): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.removeItem(KEYS.AUTH_TOKEN);
}

export function isAuthenticated(): boolean {
  return getAuthToken() === 'mr_admin_session';
}

export function getAdminPassword(): string {
  const ls = safeLocalStorage();
  if (!ls) return 'mediarabbit2024';
  return ls.getItem(KEYS.ADMIN_PASSWORD) || 'mediarabbit2024';
}

export function saveAdminPassword(password: string): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.setItem(KEYS.ADMIN_PASSWORD, password);
}

export function getFaqs(): FaqItem[] {
  const ls = safeLocalStorage();
  if (!ls) return INITIAL_DATA.faqs;
  const raw = ls.getItem(KEYS.FAQS);
  if (!raw) return INITIAL_DATA.faqs;
  return (JSON.parse(raw) as FaqItem[]).sort((a, b) => a.order - b.order);
}

export function saveFaqs(faqs: FaqItem[]): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  ls.setItem(KEYS.FAQS, JSON.stringify(faqs));
}

const DEFAULT_LEGAL_PAGES: LegalPage[] = [
  {
    id: 'agb',
    title: 'Allgemeine Geschäftsbedingungen (AGB)',
    content: `<h2>§ 1 Geltungsbereich</h2>
<p>Diese Allgemeinen Geschäftsbedingungen gelten für alle Verträge zwischen Media Rabbit GmbH (nachfolgend „Auftragnehmer") und dem Kunden (nachfolgend „Auftraggeber") über Reparaturleistungen an elektronischen Geräten.</p>

<h2>§ 2 Vertragsschluss</h2>
<p>Der Vertrag kommt durch die schriftliche oder elektronische Auftragsbestätigung des Auftragnehmers zustande. Mündliche Nebenabreden bedürfen der schriftlichen Bestätigung.</p>

<h2>§ 3 Leistungsumfang</h2>
<p>Der Auftragnehmer erbringt Reparaturleistungen gemäß dem vereinbarten Auftrag. Änderungen des Leistungsumfangs bedürfen der schriftlichen Vereinbarung.</p>

<h2>§ 4 Preise und Zahlung</h2>
<p>Es gelten die zum Zeitpunkt der Auftragserteilung gültigen Preise. Die Zahlung ist nach Abschluss der Reparatur und vor Rückgabe des Gerätes fällig.</p>

<h2>§ 5 Gewährleistung</h2>
<p>Die Gewährleistungsfrist beträgt je nach Reparaturart 3 bis 12 Monate ab Übergabe des reparierten Gerätes. Die Gewährleistung erstreckt sich auf die durchgeführten Arbeiten und verwendeten Ersatzteile.</p>

<h2>§ 6 Haftung</h2>
<p>Der Auftragnehmer haftet für Schäden nur bei Vorsatz und grober Fahrlässigkeit. Eine Haftung für mittelbare Schäden oder Datenverluste ist ausgeschlossen.</p>

<h2>§ 7 Datenschutz</h2>
<p>Die Verarbeitung personenbezogener Daten erfolgt gemäß unserer Datenschutzerklärung.</p>

<h2>§ 8 Schlussbestimmungen</h2>
<p>Es gilt das Recht der Bundesrepublik Deutschland. Gerichtsstand ist Hamburg.</p>`,
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'datenschutz',
    title: 'Datenschutzerklärung',
    content: `<h2>1. Datenschutz auf einen Blick</h2>
<p>Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie unsere Website besuchen.</p>

<h2>2. Verantwortliche Stelle</h2>
<p>Media Rabbit GmbH<br>Cremon 11<br>20457 Hamburg<br>E-Mail: info@mediarabbit.de<br>Tel.: +49 174 8323817</p>

<h2>3. Erhebung und Speicherung personenbezogener Daten</h2>
<p>Beim Besuch unserer Website werden automatisch Informationen allgemeiner Natur erfasst (Server-Log-Dateien). Diese Daten lassen keine Rückschlüsse auf Ihre Person zu.</p>

<h2>4. Reparaturanfragen</h2>
<p>Bei der Übermittlung einer Reparaturanfrage erheben wir folgende Daten: Name, E-Mail-Adresse, Telefonnummer, Gerätebeschreibung. Diese Daten werden ausschließlich zur Bearbeitung Ihrer Anfrage verwendet.</p>

<h2>5. Ihre Rechte</h2>
<p>Sie haben das Recht auf Auskunft, Berichtigung, Löschung und Einschränkung der Verarbeitung Ihrer personenbezogenen Daten. Wenden Sie sich hierzu an info@mediarabbit.de.</p>

<h2>6. Cookies</h2>
<p>Unsere Website verwendet keine Tracking-Cookies. Es werden nur technisch notwendige Cookies eingesetzt.</p>`,
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'widerruf',
    title: 'Widerrufsrecht',
    content: `<h2>Widerrufsbelehrung</h2>

<h3>Widerrufsrecht</h3>
<p>Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen. Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag des Vertragsabschlusses.</p>

<h3>Ausübung des Widerrufsrechts</h3>
<p>Um Ihr Widerrufsrecht auszuüben, müssen Sie uns (Media Rabbit GmbH, Cremon 11, 20457 Hamburg, E-Mail: info@mediarabbit.de, Tel.: +49 174 8323817) mittels einer eindeutigen Erklärung (z. B. ein mit der Post versandter Brief oder eine E-Mail) über Ihren Entschluss, diesen Vertrag zu widerrufen, informieren.</p>

<h3>Folgen des Widerrufs</h3>
<p>Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen erhalten haben, unverzüglich und spätestens binnen vierzehn Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung über Ihren Widerruf dieses Vertrags bei uns eingegangen ist.</p>

<h3>Erlöschen des Widerrufsrechts</h3>
<p>Das Widerrufsrecht erlischt bei einem Vertrag zur Erbringung von Dienstleistungen, wenn der Unternehmer die Dienstleistung vollständig erbracht hat und mit der Ausführung der Dienstleistung erst begonnen wurde, nachdem der Verbraucher dazu seine ausdrückliche Zustimmung gegeben hat.</p>

<h3>Muster-Widerrufsformular</h3>
<p>An: Media Rabbit GmbH, Cremon 11, 20457 Hamburg, info@mediarabbit.de</p>
<p>Hiermit widerrufe(n) ich/wir den von mir/uns abgeschlossenen Vertrag über die Erbringung der folgenden Dienstleistung:</p>
<p>Bestellt am: _______________<br>Name des/der Verbraucher(s): _______________<br>Anschrift des/der Verbraucher(s): _______________<br>Unterschrift: _______________ (nur bei Mitteilung auf Papier)<br>Datum: _______________</p>`,
    updatedAt: new Date().toISOString(),
  },
];

export function getLegalPages(): LegalPage[] {
  const ls = safeLocalStorage();
  if (!ls) return DEFAULT_LEGAL_PAGES;
  const raw = ls.getItem(KEYS.LEGAL_PAGES);
  if (!raw) return DEFAULT_LEGAL_PAGES;
  return JSON.parse(raw) as LegalPage[];
}

export function getLegalPage(id: LegalPage['id']): LegalPage {
  const pages = getLegalPages();
  return pages.find(p => p.id === id) || DEFAULT_LEGAL_PAGES.find(p => p.id === id)!;
}

export function saveLegalPage(page: LegalPage): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  const pages = getLegalPages();
  const idx = pages.findIndex(p => p.id === page.id);
  if (idx >= 0) {
    pages[idx] = { ...page, updatedAt: new Date().toISOString() };
  } else {
    pages.push({ ...page, updatedAt: new Date().toISOString() });
  }
  ls.setItem(KEYS.LEGAL_PAGES, JSON.stringify(pages));
}